// MAIN STORE FILE

import { createStore } from "vuex";
import axios from "axios";
import methods from "./methods";

// Helper to read API base from env and ensure trailing slash
function resolveApiBase() {
  // Fallback naar live pad (zonder 'public' conform huidig voorbeeld)
  let base = process.env.VUE_APP_API_URL || "/fuik-backend/index.php/api/";
  if (!base.endsWith("/")) {
    base = base + "/";
  }
  return base;
}

const store = createStore({
  state: {
    api_url: resolveApiBase(),
    // api_url: '/fuik-backend/public/index.php/api/',
    // api_url: '/afvangst-back/public/index.php/api/', // Todo: change line to above line
    api_headers: {}, // Wordt na login gevuld met { Authorization: 'Bearer <token>', 'App-Key': '<value>' }
    userRole: "user", // default user rol
    lifestages: ["l00", "l0", "l1", "l2", "m1", "m2", "am", "av"],
    actiontypesLookup: {},
    sessions: [],
    ponds: [],
    pondsLookup: {},
    pond: [],
    regions: [],
    session: {},
    bycatchName: [],
    parameters: [],
    users: [],
    uservalidations: { accepted: 0, rejected: 0, "to-review": 0 },
    sescatch: {},
    bycatchtypes: [],
    userOverview: [], // nieuwe losse lijst van alle gebruikers (apart van validatie)
    years: [], // beschikbare jaren uit catches-per-year
    // Time tracking state
    userYearSessions: {}, // Cache sessions per user per year { 'email_2025': [...sessions] }
    yearSessions: {}, // Cache all sessions per year { '2025': [...sessions] }
    currentTimeline: null, // Processed timeline for current view
  },
  mutations: {
    // SET  API
    SET_SESSIONS(state, sessions) {
      state.sessions = sessions;
    },
    SET_PONDS(state, ponds) {
      state.ponds = ponds;
    },
    SET_REGIONS(state, regions) {
      state.regions = regions;
    },
    SET_PONDSLOOKUP(state, pondslookup) {
      state.pondsLookup = pondslookup;
    },
    SET_POND(state, pond) {
      state.pond = pond;
    },
    SET_SESSION(state, session) {
      state.session = session;
    },
    SET_BYCATCHNAME(state, bycatchName) {
      state.bycatchName = bycatchName;
    },
    SET_PARAMETERS(state, parameters) {
      state.parameters = parameters;
    },
    SET_USERS(state, users) {
      state.users = users;
    },
    SET_CATCH(state, sescatch) {
      state.sescatch = sescatch;
    },
    SET_BYCATCHTYPES(state, bycatchtypes) {
      state.bycatchtypes = bycatchtypes;
    },
    SET_ACTIONTYPESLOOKUP(state, actiontypesLookup) {
      state.actiontypesLookup = actiontypesLookup;
    },
    SET_USER_ROLE(state, role) {
      state.userRole = role || "user";
    },
    SET_USER_OVERVIEW(state, users) {
      // nieuwe mutation voor overzicht
      state.userOverview = users || [];
    },
    SET_YEARS(state, years) {
      // beschikbare jaren voor filter
      state.years = years || [];
    },
    // Time tracking mutations
    SET_USER_YEAR_SESSIONS(state, { key, sessions }) {
      state.userYearSessions[key] = sessions;
    },
    SET_YEAR_SESSIONS(state, { year, sessions }) {
      state.yearSessions[year] = sessions;
    },
    SET_CURRENT_TIMELINE(state, timeline) {
      state.currentTimeline = timeline;
    },
  },
  actions: {
    // FETCH API
    async getSessions({ commit }) {
      const sessionsResponse = await axios.get(
        this.state.api_url + "sessions",
        {
          headers: this.state.api_headers,
        }
      );
      const sessions = sessionsResponse.data;
      commit("SET_SESSIONS", sessions);
    },
    async getPonds({ commit }) {
      const pondsResponse = await axios.get(
        this.state.api_url + "ponds/includes/latest-catches,catches-per-year",
        {
          headers: this.state.api_headers,
        }
      );
      const ponds = pondsResponse.data;
      if (!Array.isArray(ponds)) return;
      commit("SET_PONDS", ponds);

      const pondsLookup = {};
      const regions = [];
      const yearsSet = new Set();
      ponds.forEach((pond) => {
        if (pond.pond_part === null) {
          pond.pond_part = "";
        }

        pondsLookup["pond_" + pond.id] = pond;

        if (regions.indexOf(pond.region) === -1) {
          regions.push(pond.region);
        }

        // verzamel jaren uit includes["catches-per-year"]
        const cpy = pond.includes && pond.includes["catches-per-year"];
        if (Array.isArray(cpy)) {
          cpy.forEach((entry) => {
            const y = entry && (entry.year || entry.Year || entry.Y); // defensief
            if (y != null) {
              yearsSet.add(String(y));
            }
          });
        }
      });
      commit("SET_PONDSLOOKUP", pondsLookup);
      // Sort regions
      regions.sort();
      commit("SET_REGIONS", regions);

      // Sorteer jaren aflopend numeriek
      const years = Array.from(yearsSet)
        .map((y) => String(y))
        .sort((a, b) => Number(b) - Number(a));
      commit("SET_YEARS", years);
    },
    async getPond({ commit }, pondId) {
      const pondResponse = await axios.get(
        this.state.api_url +
        `ponds/${pondId}/includes/latest-catches,sessions,catches-per-year`,
        {
          headers: this.state.api_headers,
        }
      );
      const pond = pondResponse.data;
      commit("SET_POND", pond);
    },
    async getSession({ commit }, sessionId) {
      const sessionResponse = await axios.get(
        this.state.api_url + `sessions/${sessionId}`,
        {
          headers: this.state.api_headers,
        }
      );
      const session = sessionResponse.data;
      commit("SET_SESSION", session);
    },
    async getBycatchName({ commit }, year = null) {
      let url = "sessions/includes/catches,bycatches";
      if (year) {
        url += `?year=${year}`;
      }
      const bycatchNameResponse = await axios.get(
        this.state.api_url + url,
        {
          headers: this.state.api_headers,
        }
      );
      const bycatchName = bycatchNameResponse.data;
      commit("SET_BYCATCHNAME", bycatchName);
    },
    async getParameters({ commit }) {
      const parametersResponse = await axios.get(
        this.state.api_url + "parameters-per-type",
        {
          headers: this.state.api_headers,
        }
      );
      const parameters = parametersResponse.data;
      commit("SET_PARAMETERS", parameters);

      const actionTypesLookup = {};
      parameters.action_type.forEach((action_type) => {
        actionTypesLookup["action_type_" + action_type.value] =
          action_type.label;
      });
      commit("SET_ACTIONTYPESLOOKUP", actionTypesLookup);
    },
    async getUsers({ commit }) {
      const usersResponse = await axios.get(
        this.state.api_url + "validation-emails",
        {
          headers: this.state.api_headers,
        }
      );
      const users = usersResponse.data;
      commit("SET_USERS", users);
    },
    async getCatch({ commit }, id) {
      const getCatchResponse = await axios.get(
        this.state.api_url + `catches/${id}`,
        {
          headers: this.state.api_headers,
        }
      );
      const sescatch = getCatchResponse.data;
      commit("SET_CATCH", sescatch);
    },
    async getBycatchTypes({ commit }) {
      const bycatchTypesResponse = await axios.get(
        this.state.api_url + "bycatchtypes",
        {
          headers: this.state.api_headers,
        }
      );
      const bycatchtypes = bycatchTypesResponse.data;
      commit("SET_BYCATCHTYPES", bycatchtypes);
    },
    // Time tracking actions
    async getUserAllSessions({ commit, state }, email) {
      const key = `all_${email}`;
      // Return cached if available
      if (state.userYearSessions[key]) {
        return state.userYearSessions[key];
      }
      // Fetch from API (no year filter)
      const response = await axios.get(
        `${state.api_url}sessions/includes/catches?email=${encodeURIComponent(
          email
        )}`,
        { headers: state.api_headers }
      );
      const sessions = response.data;
      commit("SET_USER_YEAR_SESSIONS", { key, sessions });
      return sessions;
    },
    async getUserYearSessions({ commit, state }, { email, year }) {
      const key = `${email}_${year}`;
      // Return cached if available
      if (state.userYearSessions[key]) {
        return state.userYearSessions[key];
      }
      // Fetch from API
      const response = await axios.get(
        `${state.api_url}sessions/includes/catches?email=${encodeURIComponent(
          email
        )}&year=${year}`,
        { headers: state.api_headers }
      );
      const sessions = response.data;
      commit("SET_USER_YEAR_SESSIONS", { key, sessions });
      return sessions;
    },
    async getAllYearSessions({ commit, state }, year) {
      const key = `all_${year}`;
      // Return cached if available
      if (state.yearSessions[key]) {
        return state.yearSessions[key];
      }
      // Fetch from API
      const response = await axios.get(
        `${state.api_url}sessions/includes/catches?year=${year}`,
        { headers: state.api_headers }
      );
      const sessions = response.data;
      commit("SET_YEAR_SESSIONS", { year: key, sessions });
      return sessions;
    },
    buildDailyTimeline({ commit, state }, { email, date }) {
      const key = `all_${email}`;
      const sessions = state.userYearSessions[key] || [];

      // Filter sessions for the specific date
      const daySessions = sessions.filter((s) => s.date === date);

      // Build timeline using helper method
      const timeline = methods.buildDayTimeline(
        daySessions,
        state.pondsLookup,
        state.parameters,
      );
      commit("SET_CURRENT_TIMELINE", timeline);
      return timeline;
    },
    calculateYearlyStats({ state }, payload) {
      const year = typeof payload === "object" ? payload?.year : payload;
      const groupBy =
        typeof payload === "object" && payload?.groupBy === "client"
          ? "client"
          : "email";
      const fromDate =
        (typeof payload === "object" && payload?.fromDate) || null;
      const toDate = (typeof payload === "object" && payload?.toDate) || null;
      // Only build the per-day breakdown (used by the inline week expand) when
      // requested, to avoid heavy work for the full-year/month views.
      const withDays = typeof payload === "object" && !!payload?.withDays;
      const key = `all_${year}`;
      const sessions = state.yearSessions[key] || [];

      // Validate sessions is an array
      if (!Array.isArray(sessions) || sessions.length === 0) {
        return [];
      }

      const inPeriod = (date) => {
        if (!fromDate && !toDate) return true;
        if (fromDate && date < fromDate) return false;
        if (toDate && date > toDate) return false;
        return true;
      };

      const toGroup = (session) => {
        if (groupBy === "client") {
          const clientValue =
            session.client === null || session.client === undefined
              ? ""
              : String(session.client);
          const clientLabel = methods.getSessionClientLabel(
            clientValue,
            state.parameters,
          );
          return {
            key: `client_${clientValue}`,
            label: clientLabel || "Onbekend",
          };
        }
        const email = session.email || "onbekend";
        return { key: `email_${email}`, label: email };
      };

      // Group sessions by selected dimension and date
      const groupedDays = {};
      sessions.forEach((session) => {
        const group = toGroup(session);
        const date = session.date;
        const dayKey = `${group.key}_${date}`;

        if (!groupedDays[dayKey]) {
          groupedDays[dayKey] = {
            groupKey: group.key,
            groupLabel: group.label,
            date,
            sessions: [],
          };
        }
        groupedDays[dayKey].sessions.push(session);
      });

      // Person-hours multiplier: a session's hours count once per worker
      // (crew member). Missing/zero worker counts default to 1.
      const workersOf = (session) => {
        const w = Number(session && session.workers);
        return w > 0 ? w : 1;
      };

      // Per-session effective span: session start (registered_at) -> last catch
      // of that session. Returns hours, or null when there is no catch to
      // measure against. All effective totals are summed from these per-session
      // spans so that session -> day -> group always reconciles (the gaps
      // between sessions are deliberately not counted).
      const sessionEffectiveSpan = (s) => {
        if (!s || !s.registered_at) return null;
        const catchTimes = (s.includes?.catches || [])
          .map((c) => c.registered_at)
          .filter(Boolean)
          .sort();
        if (catchTimes.length === 0) return null;
        return methods.calculateTimeDuration(
          s.registered_at,
          catchTimes[catchTimes.length - 1],
        );
      };

      // Display-ready session info for the per-day breakdown (navigation rows).
      const decorateSession = (s) => {
        const w = workersOf(s);
        const planned =
          methods.calculateSessionDuration(s.start_time, s.end_time) || 0;
        const effective = sessionEffectiveSpan(s);
        return {
          id: s.id,
          email: s.email || "onbekend",
          pondName: methods.getPondName(s.pond_id, state.pondsLookup),
          clientLabel:
            methods.getSessionClientLabel(s.client, state.parameters) || "-",
          startTime: (s.start_time || "").substring(0, 5),
          endTime: (s.end_time || "").substring(0, 5),
          workers: w,
          plannedHours: planned * w,
          effectiveHours: effective !== null ? effective * w : null,
        };
      };

      // Calculate daily duration for each group-day
      const groupedStats = {};
      Object.values(groupedDays).forEach(
        ({ groupKey, groupLabel, date, sessions: daySessions }) => {
          if (!groupedStats[groupKey]) {
            groupedStats[groupKey] = {
              groupKey,
              groupLabel,
              groupBy,
              effectiveHours: 0,
              plannedHours: 0,
              yearlyEffectiveHours: 0,
              dayCount: 0,
              sessionCount: 0,
              days: [],
            };
            if (groupBy === "email") {
              groupedStats[groupKey].email = groupLabel;
            } else {
              groupedStats[groupKey].client = groupLabel;
            }
          }

          // Effective hours are summed from the per-session spans so every
          // level (session -> day -> group) reconciles. Two accumulators:
          //  - dayEffective: person-hours (× crew size), shown in the tables.
          //  - daySpanX1: the same spans ×1, used for the per-person year status.
          let dayEffective = 0;
          let daySpanX1 = 0;
          let dayHasEffective = false;
          daySessions.forEach((s) => {
            const span = sessionEffectiveSpan(s);
            if (span !== null) {
              dayHasEffective = true;
              daySpanX1 += span;
              dayEffective += span * workersOf(s);
            }
          });

          // Yearly effective hours always accumulate (used for status).
          // NOTE: the year status threshold (1710/1900u) is a legal norm per
          // person, so this stays un-multiplied (×1) regardless of crew size.
          groupedStats[groupKey].yearlyEffectiveHours += daySpanX1;

          // Period-scoped metrics only count days within the selected range
          if (!inPeriod(date)) return;

          // Planned person-hours for the day (each session × its crew size).
          let dayPlanned = 0;
          daySessions.forEach((s) => {
            const planned = methods.calculateSessionDuration(
              s.start_time,
              s.end_time,
            );
            if (planned) {
              dayPlanned += planned * workersOf(s);
            }
          });
          groupedStats[groupKey].plannedHours += dayPlanned;

          const dayEffectiveValue = dayHasEffective ? dayEffective : null;
          if (dayHasEffective) {
            groupedStats[groupKey].effectiveHours += dayEffective;
            groupedStats[groupKey].dayCount++;
          }

          groupedStats[groupKey].sessionCount += daySessions.length;

          // Per-day breakdown for the inline week expand (sorted, decorated).
          if (withDays) {
            groupedStats[groupKey].days.push({
              date,
              effectiveHours: dayEffectiveValue,
              plannedHours: dayPlanned,
              sessionCount: daySessions.length,
              sessions: daySessions
                .slice()
                .sort((a, b) => {
                  const t = String(a.start_time || "").localeCompare(
                    String(b.start_time || ""),
                  );
                  return t !== 0
                    ? t
                    : String(a.email || "").localeCompare(
                        String(b.email || ""),
                      );
                })
                .map(decorateSession),
            });
          }
        },
      );

      // Convert to array, status reflects the full year, then drop groups
      // with no activity in the selected period.
      const stats = Object.values(groupedStats)
        .map((group) => ({
          ...group,
          days: group.days
            .slice()
            .sort((a, b) => a.date.localeCompare(b.date)),
          status: methods.getHourThresholdStatus(group.yearlyEffectiveHours),
        }))
        .filter((group) => group.sessionCount > 0);

      return stats;
    },
  },
  getters: {
    // GET API
    getSessions(state) {
      return state.sessions;
    },
    getPonds(state) {
      return state.ponds;
    },
    getRegions(state) {
      return state.regions;
    },
    getPondsLookup(state) {
      return state.pondsLookup;
    },
    getPond(state) {
      return state.pond;
    },
    getSession(state) {
      return state.session;
    },
    getBycatchName(state) {
      return state.bycatchName;
    },
    getParameters(state) {
      return state.parameters;
    },
    getUsers(state) {
      return state.users;
    },
    getCatch(state) {
      return state.sescatch;
    },
    getBycatchTypes(state) {
      return state.bycatchtypes;
    },
    getUserRole(state) {
      return state.userRole;
    },
    getYears(state) {
      return state.years;
    },
  },
});

export default store;
